/**
 * Tile asset loader
 * Loads floor and wall PNG assets and converts to SpriteData
 */

import type { SpriteData } from "../types/index.js";
import { TILE_SIZE } from "../types/index.js";

/**
 * Load a PNG image and convert to SpriteData array
 * For floor tiles: expects 112x16 (7 patterns × 16px)
 * For wall tiles: expects 64x128 (4×4 grid of 16×32 sprites)
 */
export async function loadTilesetImage(
  url: string,
  tileWidth: number,
  tileHeight: number,
  cols: number,
  rows: number,
): Promise<SpriteData[]> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }

      ctx.drawImage(img, 0, 0);
      const sprites: SpriteData[] = [];

      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const sprite = extractSprite(
            ctx,
            col * tileWidth,
            row * tileHeight,
            tileWidth,
            tileHeight,
          );
          sprites.push(sprite);
        }
      }

      resolve(sprites);
    };

    img.onerror = () => {
      console.warn(`[TileLoader] Failed to load: ${url}`);
      resolve([]); // Return empty array on failure
    };

    img.src = url;
  });
}

/**
 * Extract a sprite from canvas image data
 */
function extractSprite(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
): SpriteData {
  const imageData = ctx.getImageData(x, y, width, height);
  const sprite: SpriteData = [];

  for (let row = 0; row < height; row++) {
    const rowData: string[] = [];
    for (let col = 0; col < width; col++) {
      const idx = (row * width + col) * 4;
      const r = imageData.data[idx];
      const g = imageData.data[idx + 1];
      const b = imageData.data[idx + 2];
      const a = imageData.data[idx + 3];

      // Alpha threshold for transparency
      if (a < 128) {
        rowData.push("");
      } else {
        rowData.push(
          `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`.toUpperCase(),
        );
      }
    }
    sprite.push(rowData);
  }

  return sprite;
}

/**
 * Load floor tiles from floors.png
 * Returns array of 7 floor pattern sprites (16×16 each)
 */
export async function loadFloorTiles(): Promise<SpriteData[]> {
  const baseUrl = import.meta.env.BASE_URL || "./";
  const url = `${baseUrl}assets/floors.png`;
  return loadTilesetImage(url, TILE_SIZE, TILE_SIZE, 7, 1);
}

/**
 * Load wall tiles from walls.png
 * Returns array of 16 wall sprites (16×32 each) indexed by bitmask
 */
export async function loadWallTiles(): Promise<SpriteData[]> {
  const baseUrl = import.meta.env.BASE_URL || "./";
  const url = `${baseUrl}assets/walls.png`;
  return loadTilesetImage(url, TILE_SIZE, TILE_SIZE * 2, 4, 4);
}
